import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';

const NAV_ITEMS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: 'LayoutDashboard',
    path: '/home-dashboard',
  },
  {
    id: 'investments',
    label: 'Investments',
    icon: 'TrendingUp',
    path: '/investment-opportunities',
    children: [
      { label: 'Opportunities', path: '/investment-opportunities' },
      { label: 'Portfolio', path: '/portfolio-tracker' },
    ],
  },
  {
    id: 'community',
    label: 'Community',
    icon: 'Users',
    path: '/community-hub',
  },
  {
    id: 'members',
    label: 'Members',
    icon: 'UserCircle',
    path: '/members',
  },
  {
    id: 'marketplace',
    label: 'Marketplace',
    icon: 'ShoppingBag',
    path: '/win-marketplace-products',
  },
  {
    id: 'profile',
    label: 'Profile',
    icon: 'User',
    path: '/onboarding-authentication',
  },
];

const BOTTOM_NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: 'LayoutDashboard', path: '/home-dashboard' },
  { id: 'investments', label: 'Invest', icon: 'TrendingUp', path: '/investment-opportunities' },
  { id: 'community', label: 'Community', icon: 'Users', path: '/community-hub' },
  { id: 'members', label: 'Members', icon: 'UserCircle', path: '/members' },
  { id: 'marketplace', label: 'Market', icon: 'ShoppingBag', path: '/win-marketplace-products' },
  { id: 'profile', label: 'Profile', icon: 'User', path: '/onboarding-authentication' },
];

export default function MainSidebar({ isCollapsed = false, onToggleCollapse }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedItem, setExpandedItem] = useState(null);
  const [prevActive, setPrevActive] = useState(null);

  const isActive = (item) => {
    if (item?.children) {
      return item?.children?.some(child => location?.pathname === child?.path) || location?.pathname === item?.path;
    }
    return location?.pathname === item?.path;
  };

  const isChildActive = (path) => location?.pathname === path;

  const handleNavClick = (item) => {
    if (item?.children && !isCollapsed) {
      setExpandedItem(expandedItem === item?.id ? null : item?.id);
    } else {
      navigate(item?.path);
      setMobileOpen(false);
    }
  };

  const handleChildClick = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location?.pathname]);

  // Auto-expand active parent
  useEffect(() => {
    NAV_ITEMS?.forEach(item => {
      if (item?.children && item?.children?.some(c => location?.pathname === c?.path)) {
        setExpandedItem(item?.id);
      }
    });
  }, [location?.pathname]);

  return (
    <>
      {/* Mobile Menu Toggle Button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 flex items-center justify-center w-11 h-11 rounded-md bg-primary text-white shadow-lg transition-all duration-250 active:scale-95"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={mobileOpen}
      >
        <Icon name={mobileOpen ? 'X' : 'Menu'} size={20} color="#FFFFFF" />
      </button>
      {/* Mobile Overlay */}
      <div
        className={`sidebar-overlay lg:hidden ${mobileOpen ? 'visible' : ''}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden="true"
      />
      {/* Sidebar */}
      <aside
        className={`sidebar fixed top-0 left-0 h-full flex flex-col
          ${isCollapsed ? 'collapsed' : ''}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
        style={{ zIndex: 'var(--z-navigation)' }}
        aria-label="Main navigation"
      >
        {/* Logo / Brand Header */}
        <div className="sidebar-header">
          <div className="sidebar-logo overflow-hidden rounded-full p-0">
            <img
              src="/assets/images/Golden_coins_Win_Logo-1772863636273.png"
              alt="WIN gold coin logo"
              style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: '50%' }}
            />
          </div>
          <div className="sidebar-brand-text">
            <div
              className="font-heading font-semibold text-white leading-tight"
              style={{ fontSize: '1.125rem', whiteSpace: 'nowrap' }}
            >
              WIN
            </div>
            <div
              className="font-caption text-white/60 leading-tight"
              style={{ fontSize: '0.6875rem', whiteSpace: 'nowrap', letterSpacing: '0.08em' }}
            >
              WOMEN INVEST NETWORK
            </div>
          </div>

          {/* Collapse Toggle (desktop only) */}
          <button
            className="hidden lg:flex ml-auto items-center justify-center w-8 h-8 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-all duration-250 flex-shrink-0 active:scale-90"
            onClick={onToggleCollapse}
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Icon name={isCollapsed ? 'ChevronRight' : 'ChevronLeft'} size={16} color="currentColor" />
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" role="navigation">
          {NAV_ITEMS?.map((item, index) => {
            const active = isActive(item);
            const isExpanded = expandedItem === item?.id;

            return (
              <div
                key={item?.id}
                className="animate-fade-in-up"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <button
                  className={`sidebar-nav-item ${active ? 'active' : ''}`}
                  onClick={() => handleNavClick(item)}
                  aria-current={active && !item?.children ? 'page' : undefined}
                  aria-expanded={item?.children ? isExpanded : undefined}
                  title={isCollapsed ? item?.label : undefined}
                  style={{
                    transition: 'background 200ms ease, color 200ms ease, transform 150ms ease',
                  }}
                >
                  <span
                    className="nav-icon"
                    style={{
                      transition: 'transform 200ms cubic-bezier(0.34,1.56,0.64,1)',
                      transform: active ? 'scale(1.15)' : 'scale(1)',
                    }}
                  >
                    <Icon
                      name={item?.icon}
                      size={20}
                      color={active ? 'var(--color-accent)' : 'currentColor'}
                      strokeWidth={active ? 2 : 1.75}
                    />
                  </span>
                  <span className="nav-label flex-1">{item?.label}</span>
                  {item?.children && !isCollapsed && (
                    <Icon
                      name={isExpanded ? 'ChevronUp' : 'ChevronDown'}
                      size={14}
                      color="currentColor"
                      className="flex-shrink-0 opacity-60"
                      style={{ transition: 'transform 200ms ease' }}
                    />
                  )}
                </button>
                {/* Sub-navigation */}
                {item?.children && isExpanded && !isCollapsed && (
                  <div className="mt-1 ml-8 space-y-1 dropdown-enter">
                    {item?.children?.map((child) => (
                      <button
                        key={child?.path}
                        className={`sidebar-nav-item text-sm ${isChildActive(child?.path) ? 'active' : ''}`}
                        style={{ height: '40px', paddingLeft: '12px', transition: 'background 200ms ease, color 200ms ease' }}
                        onClick={() => handleChildClick(child?.path)}
                        aria-current={isChildActive(child?.path) ? 'page' : undefined}
                      >
                        <span className="nav-label">{child?.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Sidebar Footer */}
        <div
          className="p-3 border-t"
          style={{ borderColor: 'rgba(212, 175, 55, 0.2)' }}
        >
          <button
            className="sidebar-nav-item w-full"
            onClick={() => navigate('/onboarding-authentication')}
            title={isCollapsed ? 'Sign Out' : undefined}
            style={{ transition: 'background 200ms ease, color 200ms ease' }}
          >
            <span className="nav-icon">
              <Icon name="LogOut" size={18} color="currentColor" strokeWidth={1.75} />
            </span>
            <span className="nav-label text-sm">Sign Out</span>
          </button>
        </div>
      </aside>
      {/* Mobile Bottom Tab Bar */}
      <nav
        className="bottom-tab-bar lg:hidden"
        role="navigation"
        aria-label="Mobile navigation"
      >
        {BOTTOM_NAV_ITEMS?.map((item) => {
          const active = location?.pathname === item?.path ||
            (item?.id === 'investments' && ['/investment-opportunities', '/portfolio-tracker']?.includes(location?.pathname));
          return (
            <button
              key={item?.id}
              className={`bottom-tab-item ${active ? 'active' : ''}`}
              onClick={() => navigate(item?.path)}
              aria-current={active ? 'page' : undefined}
              aria-label={item?.label}
            >
              <Icon
                name={item?.icon}
                size={22}
                color={active ? 'var(--color-accent)' : 'currentColor'}
                strokeWidth={active ? 2 : 1.75}
              />
              <span>{item?.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}